/**
 * AutomationEditModal Component
 *
 * Full-screen modal for editing task configurations.
 * Shows category cards and allows inline editing of each category.
 *
 * @param {Object} props
 * @param {Object} props.automation - The automation/task to edit (from dashboard)
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSave - Callback when task is saved successfully
 */

import React, { useState, useEffect } from "react";
import { HiXMark } from "react-icons/hi2";
import cloudApi from "../../../../services/cloudApi";
import useTaskEdit from "../hooks/useTaskEdit";
import { getCategoriesWithConfigs } from "../utils/configHelpers";
import CategoryEditView from "./CategoryEditView";
import TaskEditPanel from "./TaskEditPanel";
import FaviconImage from "./FaviconImage";

const AutomationEditModal = ({ automation, onClose, onSave, initialCategory }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [task, setTask] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Load full task details
  useEffect(() => {
    const loadTask = async () => {
      if (!automation?.id) return;

      try {
        setLoadError(null);
        const response = await cloudApi.getTask(automation.id);
        console.log("📥 [AutomationEditModal] Task API response:", response);

        // Handle case where API returns { task: {...} } or task directly
        const taskData = response.task || response;
        console.log("📥 [AutomationEditModal] Task data:", {
          id: taskData.id,
          task_type: taskData.task_type,
          hasRequest: !!taskData.request,
        });

        setTask(taskData);
      } catch (error) {
        console.error("Failed to load task:", error);
        setLoadError(error.message || "Failed to load task details");
      }
    };

    loadTask();
  }, [automation?.id]);

  // Initialize the task edit hook
  const {
    taskConfig,
    isLoading,
    isSaving,
    saveError,
    hasUnsavedChanges,
    updateTaskSection,
    saveTask,
  } = useTaskEdit({
    task,
    onSave: (result) => {
      if (onSave) {
        onSave(result);
      }
    },
  });

  // Get categories with their config items
  const categories = taskConfig ? getCategoriesWithConfigs(taskConfig) : [];

  // Auto-select the initial (or General) category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      const targetCategory = initialCategory
        ? categories.find((c) => c.id === initialCategory)
        : null;
      const generalCategory = categories.find((c) => c.id === "general");
      if (targetCategory || generalCategory) {
        setActiveCategory(targetCategory || generalCategory);
      }
    }
  }, [categories.length]); // Only run when categories first load

  // Handle category selection for editing
  const handleCategoryEdit = (category) => {
    setActiveCategory(category);
  };

  // Handle back from category edit
  const handleBack = () => {
    setActiveCategory(null);
  };

  // Handle config changes within a category
  const handleConfigChange = (configId, newConfig) => {
    updateTaskSection(configId, newConfig);
    // Keep metadata.title in sync with general.name
    if (configId === "general" && newConfig.name !== undefined) {
      updateTaskSection("metadata", {
        ...taskConfig.metadata,
        title: newConfig.name,
      });
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      await saveTask();
      onClose();
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  // Handle close with unsaved changes
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (!confirmed) return;
    }
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (activeCategory) {
          handleBack();
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeCategory, hasUnsavedChanges]);

  // Determine if content is still loading
  const isContentLoading = isLoading || !task;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-xl lg:max-w-3xl xl:max-w-4xl h-[75vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800/80 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/30 dark:to-gray-900">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {taskConfig?.metadata?.websiteFavicon ? (
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/80 flex items-center justify-center">
                  <FaviconImage src={taskConfig.metadata.websiteFavicon} alt="" className="w-6 h-6 rounded" />
                </div>
              ) : (
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-500/20 flex items-center justify-center">
                  <span className="text-white font-semibold text-base">
                    {(automation?.name || "A").charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate tracking-tight">
                  {automation?.name || "Untitled"}
                </h2>
                {taskConfig?.metadata?.websiteUrl ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {taskConfig.metadata.websiteUrl}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
                    Edit automation settings
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <HiXMark className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Responsive master-detail layout */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row bg-gray-50/50 dark:bg-gray-900/50">
          {isContentLoading ? (
            /* Loading state - shown inside modal */
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700"></div>
                  <div className="absolute inset-0 w-10 h-10 rounded-full border-2 border-transparent border-t-blue-500 animate-spin"></div>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 mt-4 font-medium">Loading configuration...</span>
              </div>
            </div>
          ) : loadError ? (
            /* Error state - shown inside modal */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm px-6">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                  <HiXMark className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{loadError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-sm hover:shadow transition-all"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            /* Loaded content */
            <>
              {/* Left Panel: Categories */}
              <div
                className={`
                  ${activeCategory ? "hidden lg:block" : "block"}
                  lg:w-64 lg:flex-shrink-0 lg:border-r lg:border-gray-200/80 lg:dark:border-gray-800/60
                  h-full overflow-y-auto bg-white dark:bg-gray-900
                `}
              >
                <TaskEditPanel
                  categories={categories}
                  onCategoryEdit={handleCategoryEdit}
                  selectedCategory={activeCategory}
                />
              </div>

              {/* Right Panel: Category Detail */}
              <div
                className={`
                  ${!activeCategory ? "hidden lg:flex lg:items-center lg:justify-center" : "flex-1 flex flex-col"}
                  h-full overflow-hidden bg-white dark:bg-gray-900/80
                `}
              >
                {activeCategory ? (
                  <CategoryEditView
                    categoryDisplay={activeCategory}
                    configItems={activeCategory.configItems}
                    taskConfig={taskConfig}
                    onConfigChange={handleConfigChange}
                    onBack={handleBack}
                  />
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">
                      Select a category to configure
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-gray-900/50 flex items-center justify-between">
          <div className="text-sm">
            {saveError && (
              <span className="text-red-500 dark:text-red-400 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                {saveError}
              </span>
            )}
            {hasUnsavedChanges && !saveError && (
              <span className="text-amber-600 dark:text-amber-500 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-sm shadow-blue-600/20 hover:shadow-md hover:shadow-blue-600/25 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationEditModal;
